import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { favoritesKey as FAVORITES_KEY } from '../lib/favorites';
import ToolTile from './home/tools/ToolTile';


export default function Dashboard() {
  const { user } = useAuth();

  const [tools, setTools] = useState([]);
  const [categoriesData, setCategoriesData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

  useEffect(() => {
    if (!user?.email) return;
    try {
      const raw = localStorage.getItem(FAVORITES_KEY(user.email));
      if (!raw) return;
      const ids = JSON.parse(raw);
      setFavoriteIds(Array.isArray(ids) ? ids : []);
    } catch {
      /* ignore */
    }
  }, [user?.email]);

  useEffect(() => {
    if (!user?.email) return;
    try {
      localStorage.setItem(FAVORITES_KEY(user.email), JSON.stringify(favoriteIds));
    } catch (e) {
      /* ignore */
    }
  }, [user?.email, favoriteIds]);

  const toggleFavorite = useCallback((toolId) => {
    setFavoriteIds((prev) =>
      prev.includes(toolId) ? prev.filter((id) => id !== toolId) : [...prev, toolId]
    );
  }, []);

  const favoriteTools = tools.filter((t) => favoriteIds.includes(t.id));

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setLoadError(false);

    // The tools call drives loading/error UI; categories are decorative (icons
    // only), so a categories failure must NOT surface as a page error.
    const toolsReq = axios.get(`${API_URL}/api/my/tools`, { withCredentials: true });
    const catsReq = axios.get(`${API_URL}/api/categories`, { withCredentials: true })
      .then(res => { if (!cancelled) setCategoriesData(res.data); })
      .catch(err => { console.error(err); if (!cancelled) setCategoriesData([]); });

    toolsReq
      .then(res => { if (!cancelled) setTools(res.data); })
      .catch(err => {
        console.error(err);
        if (!cancelled) { setTools([]); setLoadError(true); }
      })
      .finally(() => {
        // Wait for the decorative categories call too so icons don't pop in late.
        Promise.resolve(catsReq).finally(() => { if (!cancelled) setIsLoading(false); });
      });

    return () => { cancelled = true; };
  }, [API_URL, reloadKey]);

  const categoryIconMap = categoriesData.reduce((acc, cat) => {
    acc[cat.name] = cat.icon;
    return acc;
  }, {});

  // Group tools by category (tools are only the logged-in user's from /api/my/tools,
  // so we only ever see categories that have at least one tool assigned to the user)
  const categories = Array.from(
    tools.reduce((map, tool) => {
      const catName = tool.category || 'Uncategorized';
      if (!map.has(catName)) {
        map.set(catName, { name: catName, icon: '', tools: [] });
      }
      map.get(catName).tools.push(tool);
      return map;
    }, new Map()).values()
  );

  // Filter by search, then hide categories with no tools (none should remain for "my" tools, but safe-guard)
  const filteredCategories = categories
    .map(category => ({
      ...category,
      tools: category.tools.filter(tool => {
        const search = searchTerm.toLowerCase();
        return (
          tool.name.toLowerCase().includes(search) ||
          (tool.description || '').toLowerCase().includes(search) ||
          (tool.category || '').toLowerCase().includes(search)
        );
      })
    }))
    .filter(category => category.tools.length > 0);

  return (
    <div className="container page-fill">
      <div className="section-header page-head">
        <div className="page-title-row">
          <h1 className="page-title">My tools</h1>
          <span className="page-subtitle">Everything you have access to, in one place.</span>
        </div>
        <div className="search-box">
          <svg className="search-icon" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <input
            type="text"
            placeholder="Search applications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      <div className="page-scroll-body">
      {isLoading ? (
        <div className="state-panel">
          <div className="spinner" />
          <span className="sr-only">Loading your tools…</span>
        </div>
      ) : loadError ? (
        <div className="state-panel" role="alert">
          <svg className="state-panel__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <h3 className="state-panel__title">Couldn’t load your tools</h3>
          <p className="state-panel__body">Something went wrong reaching the server. Please try again in a moment.</p>
          <button type="button" className="btn btn-primary" onClick={() => setReloadKey((k) => k + 1)}>
            Retry
          </button>
        </div>
      ) : tools.length === 0 ? (
        <div className="state-panel">
          <svg className="state-panel__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" />
          </svg>
          <h3 className="state-panel__title">No tools yet</h3>
          <p className="state-panel__body">You don’t have access to any tools right now. Request access to the tools you need and they’ll show up here.</p>
          <Link to="/requests" className="btn btn-primary">Request access</Link>
        </div>
      ) : (
      <>
      {favoriteTools.length > 0 && (
        <div className="favorite-apps-panel">
          <h3 className="favorite-apps-title">Favorite tools</h3>
          <div className="favorite-apps-grid">
            {favoriteTools.map((tool) => (
              <ToolTile key={tool.id} tool={tool} isFavorite onToggleFavorite={toggleFavorite} />
            ))}
          </div>
        </div>
      )}

      <div className="categories-grid">
        {filteredCategories.map(category => (
          <div className="category-section" key={category.name}>
            <h3 className="category-title">
              {categoryIconMap[category.name] && (
                <span aria-hidden="true" dangerouslySetInnerHTML={{ __html: categoryIconMap[category.name] }} />
              )}
              {category.name}
            </h3>
            <div className="apps-grid">
              {category.tools.map((tool) => (
                <ToolTile
                  key={tool.id}
                  tool={tool}
                  isFavorite={favoriteIds.includes(tool.id)}
                  onToggleFavorite={toggleFavorite}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
      </>
      )}
      </div>
    </div>
  );
}
